// src/pages/buyers/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  Divider,
  alpha,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Storefront as StorefrontIcon,
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Brightness1 as Brightness1Icon,
} from '@mui/icons-material';
import { BRAND_COLORS } from '../components/styles';
import logo from '../../../assets/images.png';

// ============================================
// SIDEBAR COMPONENT
// ============================================

const Sidebar = ({
  open,
  onClose,
  isMobile,
  selectedTab,
  onTabChange,
  userData,
  wishlistCount,
  cartItemCount,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get user name from localStorage
  const userName = localStorage.getItem('user_name') || 
                   localStorage.getItem('userName') || 
                   userData?.name || 
                   userData?.display_name ||
                   userData?.full_name ||
                   'Buyer';

  // Get organisation from localStorage
  const organisation = localStorage.getItem('organisation') || 
                       userData?.organisation || 
                       userData?.role_specific_data?.organisation ||
                       userData?.role_specific_data?.company_name ||
                       '';

  // Menu items with paths
  const menuItems = [
    { path: '/buyer/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/', label: 'Chemical DB', icon: <StorefrontIcon /> },
    { path: '/buyer/orders', label: 'My Chemicals', icon: <ShoppingCartIcon /> },
  
    { path: '/buyer/SupplierManagement', label: 'Suppliers', icon: <BusinessIcon /> },
 
  ];

  // Sync selected tab with current route
  useEffect(() => {
    const currentPath = location.pathname;
    const matchingItem = menuItems.find(item => item.path === currentPath);
    if (matchingItem) {
      onTabChange(matchingItem.path);
    }
  }, [location.pathname]);

  // ============================================
  // BLINKING ANIMATED BRAND COMPONENT
  // ============================================

  const AnimatedBrand = () => {
    const [colorIndex, setColorIndex] = useState(0);
    const [scale, setScale] = useState(1);
    
    const brandColors = [
      '#C62828', // Deep Red
      '#2E7D32', // Forest Green
      '#F57F17', // Golden Amber
      '#1565C0', // Royal Blue
      '#6A1B9A', // Deep Purple
      '#D84315', // Burnt Orange
      '#00838F', // Teal
    ];

    useEffect(() => {
      const interval = setInterval(() => {
        setColorIndex(prev => (prev + 1) % brandColors.length);
        setScale(1 + Math.sin(Date.now() / 300) * 0.02);
      }, 600);
      return () => clearInterval(interval);
    }, []);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120%',
            height: '120%',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${brandColors[colorIndex]}20, transparent 70%)`,
            animation: 'glowPulse 1.2s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
        
        <Typography
          variant="h6"
          fontWeight={600}
        >
          ChemDb
          <Brightness1Icon
            sx={{
              fontSize: 10,
              color: brandColors[colorIndex],
              animation: 'pulse 0.8s ease-in-out infinite',
              filter: `drop-shadow(0 0 12px ${brandColors[colorIndex]}60)`,
            }}
          />
        </Typography>

        <style>
          {`
            @keyframes textGlow {
              0%, 100% {
                text-shadow: 0 0 40px ${brandColors[0]}50, 
                             0 0 80px ${brandColors[0]}25,
                             0 0 120px ${brandColors[0]}12,
                             0 4px 20px rgba(0,0,0,0.1);
              }
              50% {
                text-shadow: 0 0 60px ${brandColors[0]}70, 
                             0 0 100px ${brandColors[0]}40,
                             0 0 150px ${brandColors[0]}20,
                             0 4px 30px rgba(0,0,0,0.15);
              }
            }
            @keyframes glowPulse {
              0%, 100% {
                transform: translate(-50%, -50%) scale(0.8);
                opacity: 0.3;
              }
              50% {
                transform: translate(-50%, -50%) scale(1.2);
                opacity: 0.6;
              }
            }
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 0.8;
              }
              50% {
                transform: scale(1.3);
                opacity: 1;
              }
            }
          `}
        </style>
      </Box>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Brand - Modern gradient background */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          pb: 1,
          borderBottom: '1px solid #E5E7EB',
          background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.03), rgba(198, 40, 40, 0.03))',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, transparent, rgba(46, 125, 50, 0.03), transparent)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <img 
            src={logo} 
            alt="Logo" 
            style={{ 
              height: 36, 
              borderRadius: 10,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              padding: 2,
              background: 'white',
            }} 
          />
          <AnimatedBrand />
        </Box>
        
        <style>
          {`
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}
        </style>
      </Box>

      {/* User Info - Modern card design */}
      <Box
        sx={{
          p: 2,
          mx: 1.5,
          my: 1.5,
          bgcolor: '#F8FAFC',
          borderRadius: 3,
          border: '1px solid rgba(46, 125, 50, 0.08)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.02)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            borderColor: 'rgba(46, 125, 50, 0.15)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              background: 'linear-gradient(135deg, #2E7D32, #4CAF50)',
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: '0 2px 12px rgba(46, 125, 50, 0.25)',
            }}
          >
            {userName?.charAt(0)?.toUpperCase() || 'B'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle1"
              fontWeight={700} 
              noWrap
              sx={{
                color: '#1a1a1a',
                letterSpacing: '0.2px',
                fontSize: '1rem',
                fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
              }}
            >
              {userName}
            </Typography>
            <Typography 
              variant="body2"
              noWrap
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#4B5563',
                fontWeight: 500,
                fontSize: '0.8rem',
                fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: BRAND_COLORS.success,
                  display: 'inline-block',
                  animation: 'statusPulse 2s ease-in-out infinite',
                  flexShrink: 0,
                }}
              />
              {organisation || 'Individual Buyer'}
            </Typography>
          </Box>
          <Chip
            label="Active"
            size="small"
            sx={{
              bgcolor: alpha(BRAND_COLORS.success, 0.12),
              color: BRAND_COLORS.success,
              fontWeight: 700,
              fontSize: '0.6rem',
              height: 22,
              fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        </Box>
        
        <style>
          {`
            @keyframes statusPulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.4; transform: scale(0.7); }
            }
          `}
        </style>
      </Box>

      {/* Menu - Using paths with useNavigate */}
      <List sx={{ flex: 1, px: 1.5, pt: 0.5 }}>
        {menuItems.map((item) => {
          // Check if this item is the currently selected one
          const isSelected = selectedTab === item.path || location.pathname === item.path;
          
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  // Navigate to the path
                  navigate(item.path);
                  // Update selected state
                  onTabChange(item.path);
                  // Close drawer on mobile
                  if (isMobile) onClose();
                }}
                sx={{
                  borderRadius: 2,
                  py: 0.9,
                  px: 1.5,
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: isSelected ? alpha(BRAND_COLORS.primary, 0.08) : 'transparent',
                  // Flat button style - no background by default
                  '&:hover': {
                    backgroundColor: alpha(BRAND_COLORS.primary, 0.06),
                    transform: 'translateX(3px)',
                    '& .MuiListItemIcon-root': {
                      color: BRAND_COLORS.primary,
                    },
                  },
                  // Active state - subtle but clear selection
                  '&.Mui-selected': {
                    backgroundColor: alpha(BRAND_COLORS.primary, 0.08),
                    '& .MuiListItemIcon-root': {
                      color: BRAND_COLORS.primary,
                    },
                    '& .MuiListItemText-primary': {
                      color: BRAND_COLORS.primary,
                      fontWeight: 700,
                    },
                    // Left accent bar
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3.5,
                      height: 24,
                      borderRadius: '0 4px 4px 0',
                      background: `linear-gradient(180deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryLight})`,
                      boxShadow: `0 2px 8px ${alpha(BRAND_COLORS.primary, 0.3)}`,
                    },
                    // Active indicator dot
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: BRAND_COLORS.primary,
                      boxShadow: `0 0 12px ${alpha(BRAND_COLORS.primary, 0.4)}`,
                      animation: 'activeDotPulse 1.5s ease-in-out infinite',
                    },
                  },
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40, 
                    color: isSelected ? BRAND_COLORS.primary : '#6B7280',
                    transition: 'all 0.25s ease',
                    '& .MuiSvgIcon-root': {
                      fontSize: 22,
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: isSelected ? 700 : 600,
                    color: isSelected ? BRAND_COLORS.primary : '#1F2937',
                    transition: 'all 0.25s ease',
                    fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
                    letterSpacing: '0.2px',
                  }}
                />
                {item.badge > 0 && (
                  <Badge
                    badgeContent={item.badge}
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: item.path === '/buyer/wishlist' ? BRAND_COLORS.error : BRAND_COLORS.primary,
                        color: 'white',
                        fontWeight: 800,
                        fontSize: '0.7rem',
                        height: 20,
                        minWidth: 20,
                        borderRadius: 10,
                        boxShadow: `0 2px 8px ${item.path === '/buyer/wishlist' ? 'rgba(198, 40, 40, 0.3)' : 'rgba(46, 125, 50, 0.3)'}`,
                        fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ opacity: 0.4, mx: 1.5 }} />

      {/* Logout - Flat button design with dark text */}
      <Box sx={{ p: 1.5 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              py: 0.9,
              px: 1.5,
              transition: 'all 0.25s ease',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: alpha(BRAND_COLORS.error, 0.06),
                transform: 'translateX(3px)',
                '& .MuiListItemIcon-root': {
                  color: BRAND_COLORS.error,
                },
                '& .MuiListItemText-primary': {
                  color: BRAND_COLORS.error,
                },
              },
            }}
          >
            <ListItemIcon 
              sx={{ 
                minWidth: 40, 
                color: '#6B7280',
                transition: 'all 0.25s ease',
                '& .MuiSvgIcon-root': {
                  fontSize: 22,
                },
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: '0.95rem',
                color: '#1F2937',
                fontWeight: 600,
                transition: 'all 0.25s ease',
                fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
                letterSpacing: '0.2px',
              }}
            />
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: alpha(BRAND_COLORS.error, 0.04),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: '#9CA3AF',
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>
          </ListItemButton>
        </ListItem>
      </Box>

      {/* Footer - Version info */}
      <Box
        sx={{
          p: 1.2,
          textAlign: 'center',
          borderTop: '1px solid #E5E7EB',
          bgcolor: alpha('#000', 0.01),
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#6B7280',
            fontSize: '0.6rem',
            letterSpacing: 0.5,
            fontWeight: 600,
            fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
          }}
        >
          v2.4.0 • Secure Connection
        </Typography>
      </Box>

      <style>
        {`
          @keyframes activeDotPulse {
            0%, 100% {
              opacity: 1;
              transform: translateY(-50%) scale(1);
            }
            50% {
              opacity: 0.5;
              transform: translateY(-50%) scale(0.7);
            }
          }
        `}
      </style>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      sx={{
        width: 280,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          borderRight: '1px solid rgba(0,0,0,0.04)',
          bgcolor: '#FFFFFF',
          boxShadow: '2px 0 20px rgba(0,0,0,0.02)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;